import {
	TaskCTAType,
	TaskStatus,
	TaskStatusEnum,
	UpdateTaskDto,
} from "@dune/types";
import {
	Avatar,
	AvatarFallback,
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	FileIcon,
	FileTypeLabel,
	Input,
	TextArea,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@dune/ui";
import { formatDateForInput, formatSimpleDate } from "@dune/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
	Calendar,
	CalendarX,
	CheckCircle2,
	ChevronDown,
	CircleAlert,
	Link2Off,
	Loader2,
	RotateCcw,
	Save,
	Trash2,
	UserCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { privateAxios } from "../../../../api/axios";
import { getDocumentUrl } from "../../../../api/queries/files.queries";
import { useMe } from "../../../../api/queries/users.queries";
import {
	useDeleteTask,
	useDeleteTaskForGFA,
	useDetachDocumentFromTask,
	useGetAssignableUsers,
	useUpdateTaskForConstructionSite,
	useUpdateTaskForGFA,
} from "../../../../api/queries/tasks.queries";
import { useAttachDocumentToTask } from "../../../../api/mutations/tasks.mutations";
import { constructionSitesQueryKeys } from "../../../../api/query-keys/constructionSites.query-keys";
import { gfasQueryKeys } from "../../../../api/query-keys/gfas.query-keys";
import { replacePlaceholders } from "../../../../utils/pathUtils";

import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../../../component/common/ConfirmationDialog";
import { usePanel } from "../../../../hooks/usePanel";
import { useDocumentStore } from "../../../../stores/document.store";
import { useTaskActions } from "../../../../stores/task.store";
import type { TaskDetailsPanelProps } from "../../../../types/panel.types";
import AnalyzeGFASubmissionModal from "../modals/AnalyzeGFASubmissionModal";
import GenerateGFAConstructionSiteDocumentsModal from "../../../gfa/components/modals/GenerateGFAConstructionSiteDocumentsModal";
import TaskUploadDocumentModal from "../../../../component/common/TaskUploadDocumentModal";

interface TaskFormData {
	title?: string;
	description: string;
	assigneeId: number | null;
	status?: TaskStatus;
	dueDate?: string;
}

const getTaskStatusLabel = (status: string): string => {
	switch (status) {
		case "TO_DO":
			return "À faire";
		case "IN_PROGRESS":
			return "En cours";
		case "DONE":
			return "Terminé";
		default:
			return status;
	}
};

const getTaskStatusColorClasses = (status: string): string => {
	switch (status) {
		case "TO_DO":
			return "bg-gray-100 text-gray-800 border-gray-300";
		case "IN_PROGRESS":
			return "bg-blue-100 text-blue-800 border-blue-300";
		case "DONE":
			return "bg-green-100 text-green-800 border-green-300";
		default:
			return "bg-gray-100 text-gray-800 border-gray-300";
	}
};

const ConstructionSiteTaskDetailsPanel: React.FC<TaskDetailsPanelProps> = ({
	task,
	entityId,
	entityType,
}) => {
	const isGFA = entityType === "GFA";
	const entityIdString = entityId.toString();
	const navigate = useNavigate();
	const { me } = useMe();
	const isDuneUser = me?.role === "DUNE";
	const { data: assignableUsers } = useGetAssignableUsers();
	const updateTaskMutationForConstructionSite =
		useUpdateTaskForConstructionSite(entityIdString);
	const updateTaskMutationForGFA = useUpdateTaskForGFA(entityIdString);
	const updateTaskMutation = isGFA
		? updateTaskMutationForGFA
		: updateTaskMutationForConstructionSite;
	const deleteTaskMutationForConstructionSite = useDeleteTask(entityId);
	const deleteTaskMutationForGFA = useDeleteTaskForGFA(entityIdString);
	const deleteTaskMutation = isGFA
		? deleteTaskMutationForGFA
		: deleteTaskMutationForConstructionSite;
	const detachDocumentMutation = useDetachDocumentFromTask(
		isGFA ? undefined : entityId,
	);
	const attachDocumentMutation = useAttachDocumentToTask(
		undefined,
		undefined,
		isGFA ? undefined : entityIdString,
		undefined,
		isGFA ? entityIdString : undefined,
	);
	const { clearPanel } = usePanel();
	const { clearTaskSelection } = useTaskActions();
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState(false);
	const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
		useState(false);
	const [isCtaLoading, setIsCtaLoading] = useState(false);
	const [isAnalyzeGfaSubmissionModalOpen, setIsAnalyzeGfaSubmissionModalOpen] =
		useState(false);
	const [isGenerateGfaDocumentModalOpen, setIsGenerateGfaDocumentModalOpen] =
		useState(false);
	const queryClient = useQueryClient();
	const selectDocument = useDocumentStore((state) => state.selectDocument);

	const form = useForm<TaskFormData>({
		defaultValues: {
			title: task.title,
			description: task.description || "",
			assigneeId: task.assigneeId,
			status: task.status,
			dueDate: formatDateForInput(task.dueDate?.toString()),
		},
	});

	const {
		control,
		handleSubmit,
		reset,
		formState: { isDirty, isValid },
		setValue,
		watch,
	} = form;

	useEffect(() => {
		reset({
			title: task.title,
			description: task.description || "",
			assigneeId: task.assigneeId,
			status: task.status,
			dueDate: formatDateForInput(task.dueDate?.toString()),
		});
	}, [task, reset]);

	const onSubmit = async (data: TaskFormData) => {
		const updateData: UpdateTaskDto = {};

		if (data.title !== task.title) {
			updateData.title = data.title;
		}

		if (data.description !== task.description) {
			updateData.description = data.description;
		}

		if (data.assigneeId !== task.assigneeId) {
			updateData.assigneeId = data.assigneeId;
		}

		if (data.status !== task.status) {
			updateData.status = data.status;
		}

		const initialDueDateStr = formatDateForInput(task.dueDate?.toString());
		if ((data.dueDate ?? "") !== initialDueDateStr) {
			updateData.dueDate = data.dueDate
				? new Date(data.dueDate).toISOString()
				: null;
		}

		if (Object.keys(updateData).length > 0) {
			await updateTaskMutation.mutateAsync({
				taskId: task.id,
				updateData,
			});

			reset({
				title: data.title,
				description: data.description,
				assigneeId: data.assigneeId,
				status: data.status,
				dueDate: data.dueDate ?? formatDateForInput(task.dueDate?.toString()),
			});
		}
	};

	const handleReset = () => {
		reset();
	};

	const handleDeleteTask = () => {
		setIsDeleteConfirmationOpen(true);
	};

	const handleConfirmDelete = async () => {
		try {
			await deleteTaskMutation.mutateAsync(task.id);
			setIsDeleteConfirmationOpen(false);

			await queryClient.invalidateQueries({
				queryKey: isGFA
					? gfasQueryKeys.tasks(entityIdString)
					: constructionSitesQueryKeys.tasks(entityIdString),
			});

			clearTaskSelection();
			clearPanel();

			toast.success("Tâche supprimée avec succès");
		} catch (error) {
			console.error("Error deleting task:", error);
			toast.error("Erreur lors de la suppression de la tâche");
		}
	};

	const handleCTAClick = async () => {
		if (!task.cta) return;
		try {
			switch (task.cta.actionType) {
				case TaskCTAType.REDIRECT:
					if (task.cta.navigationPath) {
						const placeholders: Record<string, string> = isGFA
							? {
									gfaId: entityIdString,
									id: entityIdString,
								}
							: {
									constructionSiteId: entityIdString,
									id: entityIdString,
								};
						const path = replacePlaceholders(
							task.cta.navigationPath,
							placeholders,
						);
						navigate(path);
					}
					break;

				case TaskCTAType.ENDPOINT:
					if (task.cta.endpoint) {
						setIsCtaLoading(true);
						try {
							const endpointData = isGFA
								? { gfaId: entityId, taskId: task.id }
								: { constructionSiteId: entityId, taskId: task.id };
							const response = await privateAxios.post(
								task.cta.endpoint,
								endpointData,
							);

							if (response.data?.message) {
								toast.success(response.data.message);
							} else {
								toast("Action exécutée avec succès");
							}

							await queryClient.invalidateQueries({
								queryKey: isGFA
									? gfasQueryKeys.tasks(entityIdString)
									: constructionSitesQueryKeys.tasks(entityIdString),
							});
							await queryClient.invalidateQueries({
								queryKey: ["task", task.id],
							});
						} finally {
							setIsCtaLoading(false);
						}
					}
					break;

				case TaskCTAType.MODAL:
					if (task.cta.modalKey === "analyze-gfa-submission") {
						setIsAnalyzeGfaSubmissionModalOpen(true);
					} else if (task.cta.modalKey === "generate-gfa-document") {
						setIsGenerateGfaDocumentModalOpen(true);
					} else {
						console.warn(
							`Modales CTA non supportées pour ${isGFA ? "les GFA" : "les chantiers"}:`,
							task.cta.modalKey,
						);
					}
					break;

				default:
					console.warn("Type d'action CTA non supporté:", task.cta.actionType);
			}
		} catch (error) {
			console.error("Erreur lors de l'exécution de l'action CTA:", error);
			toast("Erreur lors de l'exécution de l'action", {
				description: "Une erreur est survenue. Veuillez réessayer.",
			});
			setIsCtaLoading(false);
		}
	};

	const handleDetachDocument = async (fileId: string) => {
		try {
			await detachDocumentMutation.mutateAsync({
				taskId: task.id,
				fileId,
			});
			if (isGFA) {
				await queryClient.invalidateQueries({
					queryKey: gfasQueryKeys.tasks(entityIdString),
				});
			} else {
				await queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.tasks(entityIdString),
				});
			}
		} catch (error) {
			console.error("Error detaching document:", error);
		}
	};

	const handleAttachDocument = async (fileId: string) => {
		await attachDocumentMutation.mutateAsync({
			taskId: task.id,
			fileId,
		});

		if (isGFA) {
			await queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.tasks(entityIdString),
				refetchType: "active",
			});
		} else {
			await queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.tasks(entityIdString),
				refetchType: "active",
			});
		}
	};

	const selectedAssignee = watch("assigneeId")
		? assignableUsers?.find((user) => user.id === watch("assigneeId"))
		: null;

	const statusOptions = [
		{ value: TaskStatusEnum.TO_DO, label: "À faire" },
		{ value: TaskStatusEnum.IN_PROGRESS, label: "En cours" },
		{ value: TaskStatusEnum.DONE, label: "Terminé" },
	];

	return (
		<>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					{task.isEditable ? (
						<Controller
							name="title"
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									className="text-xl font-bold text-gray-900 border-0 p-0 shadow-none focus:ring-0 bg-transparent"
									placeholder="Titre de la tâche"
								/>
							)}
						/>
					) : (
						<h3 className="text-xl font-bold text-gray-900">{task.title}</h3>
					)}
					{isDuneUser ? (
						<Controller
							name="status"
							control={control}
							render={({ field }) => (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button
											type="button"
											className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-md"
										>
											<Badge
												className={`cursor-pointer inline-flex items-center gap-1 ${getTaskStatusColorClasses(field.value ?? "")}`}
											>
												{getTaskStatusLabel(field.value ?? "")}
												<ChevronDown className="h-3.5 w-3.5 opacity-80" />
											</Badge>
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										{statusOptions.map((option) => (
											<DropdownMenuItem
												key={option.value}
												onClick={() =>
													field.onChange(option.value as TaskStatus, {
														shouldDirty: true,
													})
												}
											>
												{option.label}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						/>
					) : (
						<Badge className={getTaskStatusColorClasses(task.status)}>
							{getTaskStatusLabel(task.status)}
						</Badge>
					)}
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
					{/* Informations */}
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
						{/* Due date */}
						<div>
							<span className="mb-1 block text-sm font-medium text-gray-500">
								Date d'échéance
							</span>
							{isDuneUser ? (
								<Controller
									name="dueDate"
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											type="date"
											value={field.value ?? ""}
											onChange={(e) =>
												field.onChange(e.target.value || undefined, {
													shouldDirty: true,
												})
											}
										/>
									)}
								/>
							) : (
								<div className="flex items-center gap-2 text-sm text-gray-700">
									{task.status === "DONE" ? (
										task.dueDate &&
										task.completedAt &&
										new Date(task.completedAt) > new Date(task.dueDate) ? (
											<>
												<CircleAlert className="h-4 w-4 text-orange-500 flex-shrink-0" />
												<span className="text-orange-600">
													{formatSimpleDate(task.dueDate)}
												</span>
											</>
										) : (
											<>
												<CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
												<span className="text-green-600">
													{task.dueDate ? formatSimpleDate(task.dueDate) : "-"}
												</span>
											</>
										)
									) : task.dueDate ? (
										new Date(task.dueDate) < new Date() ? (
											<>
												<CalendarX className="h-4 w-4 text-red-500 flex-shrink-0" />
												<span className="text-red-600">
													{formatSimpleDate(task.dueDate)}
												</span>
											</>
										) : (
											<>
												<Calendar className="h-4 w-4 text-blue-500 flex-shrink-0" />
												<span className="text-green-600">
													{formatSimpleDate(task.dueDate)}
												</span>
											</>
										)
									) : (
										<>
											<Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
											<span className="text-gray-500">-</span>
										</>
									)}
								</div>
							)}
						</div>

						{/* Start / End */}
						<div>
							<span className="mb-1 block text-sm font-medium text-gray-500">
								Début/Fin
							</span>
							<div className="space-y-1">
								<div className="flex items-center gap-2 text-sm text-gray-700">
									<Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
									<span className="text-xs text-gray-500">Début:</span>
									{formatSimpleDate(task.createdAt)}
								</div>
								{task.completedAt && (
									<div className="flex items-center gap-2 text-sm text-gray-700">
										<Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
										<span className="text-xs text-gray-500">Fin:</span>
										{formatSimpleDate(task.completedAt)}
									</div>
								)}
							</div>
						</div>

						{/* Assignee */}
						<div>
							<span className="mb-1 block text-sm font-medium text-gray-500">
								Assigné à
							</span>
							<div className="flex items-center gap-2">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button
											type="button"
											className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
										>
											<Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-2 transition-all">
												{selectedAssignee ? (
													<AvatarFallback className="text-sm bg-blue-500 text-white">
														{selectedAssignee.firstname.charAt(0)}
														{selectedAssignee.lastname.charAt(0)}
													</AvatarFallback>
												) : (
													<AvatarFallback className="text-sm bg-gray-100 border-2 border-dashed border-blue-400">
														<UserCircle className="h-5 w-5 text-blue-400" />
													</AvatarFallback>
												)}
											</Avatar>
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent className="z-50" align="start">
										<DropdownMenuItem
											onClick={() =>
												setValue("assigneeId", null, { shouldDirty: true })
											}
										>
											<Avatar className="h-4 w-4 mr-2">
												<AvatarFallback className="text-xs bg-gray-100">
													<UserCircle className="h-3 w-3 text-gray-400" />
												</AvatarFallback>
											</Avatar>
											Non assigné
										</DropdownMenuItem>
										{assignableUsers?.map((user) => (
											<DropdownMenuItem
												key={user.id}
												onClick={() =>
													setValue("assigneeId", user.id, { shouldDirty: true })
												}
											>
												<Avatar className="h-4 w-4 mr-2">
													<AvatarFallback className="text-xs">
														{user.firstname.charAt(0)}
														{user.lastname.charAt(0)}
													</AvatarFallback>
												</Avatar>
												{user.firstname} {user.lastname}
												<span className="ml-2 text-xs text-gray-500">
													({user.role})
												</span>
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
								{selectedAssignee && (
									<span className="text-sm text-gray-700">
										{selectedAssignee.firstname} {selectedAssignee.lastname}
									</span>
								)}
							</div>
						</div>
					</div>

					{/* Actions */}
					{task.cta && (
						<div className="space-y-2 flex flex-col items-start">
							<span className="text-sm font-medium text-gray-500">Actions</span>
							<Button
								type="button"
								onClick={handleCTAClick}
								disabled={isCtaLoading}
							>
								{isCtaLoading ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Traitement en cours...
									</>
								) : (
									task.cta.label
								)}
							</Button>
						</div>
					)}

					{/* Documents liés */}
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-gray-500">
								Documents liés
							</span>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => setIsUploadDocumentModalOpen(true)}
							>
								Ajouter un document
							</Button>
						</div>
						{task.files && task.files.length > 0 && (
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
								<div className="space-y-2">
									{task.files.map((taskFile) => (
										<div
											key={taskFile.file.id}
											className="flex items-center gap-3 rounded-md bg-white p-2 shadow-sm"
										>
											<div className="flex-shrink-0">
												<FileIcon
													mimeType={taskFile.file.mimeType}
													className="h-5 w-5"
												/>
											</div>
											<div className="flex-1 min-w-0">
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger asChild>
															<p className="text-sm font-medium text-gray-900 truncate cursor-help max-w-[220px]">
																{taskFile.file.name}
															</p>
														</TooltipTrigger>
														<TooltipContent>
															<p className="max-w-xs break-words">
																{taskFile.file.name}
															</p>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
												<FileTypeLabel
													mimeType={taskFile.file.mimeType}
													className="text-gray-500"
												/>
											</div>
											<div className="flex items-center gap-2">
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={async () => {
														try {
															const url = await getDocumentUrl({
																bucket: "private",
																key: taskFile.file.key,
															});

															if (url) {
																selectDocument(
																	url,
																	taskFile.file.id,
																	taskFile.file.mimeType,
																	taskFile.file.name,
																);
															} else {
																toast.error(
																	"Impossible de récupérer l'URL du document",
																);
															}
														} catch (error) {
															console.error(
																"TaskDetailsPanel - Error getting document URL:",
																error,
															);
															toast.error(
																"Erreur lors de l'ouverture du document",
															);
														}
													}}
												>
													Voir
												</Button>
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() =>
																	handleDetachDocument(taskFile.file.id)
																}
																disabled={detachDocumentMutation.isPending}
															>
																{detachDocumentMutation.isPending ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<Link2Off className="h-4 w-4" />
																)}
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															<p>Dissocier le document de la tâche</p>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Comment */}
					<div>
						<Controller
							name="description"
							control={control}
							render={({ field }) => (
								<TextArea
									{...field}
									label="Commentaire"
									placeholder="Rédiger un commentaire"
									className="min-h-[120px] resize-none align-top"
									style={{ textAlign: "left", verticalAlign: "top" }}
								/>
							)}
						/>
					</div>

					{/* Footer */}
					<div className="sticky bottom-0 pt-4 border-t bg-white">
						<div className="flex justify-between items-center">
							{/* Delete button - Only for manual tasks */}
							{task.isEditable && (
								<Button
									type="button"
									variant="destructive"
									onClick={handleDeleteTask}
									disabled={deleteTaskMutation.isPending}
									className="flex items-center gap-2"
								>
									{deleteTaskMutation.isPending ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" />
											Suppression...
										</>
									) : (
										<>
											<Trash2 className="h-4 w-4" />
											Supprimer
										</>
									)}
								</Button>
							)}

							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									onClick={handleReset}
									disabled={!isDirty || updateTaskMutation.isPending}
								>
									<RotateCcw className="h-4 w-4 mr-2" />
									Reset
								</Button>
								<Button
									type="submit"
									disabled={
										!isDirty || !isValid || updateTaskMutation.isPending
									}
								>
									{updateTaskMutation.isPending ? (
										"Sauvegarde..."
									) : (
										<>
											<Save className="h-4 w-4 mr-2" />
											Sauvegarder
										</>
									)}
								</Button>
							</div>
						</div>
					</div>
				</form>
			</div>

			<ConfirmationDialog
				open={isDeleteConfirmationOpen}
				onOpenChange={setIsDeleteConfirmationOpen}
				title="Supprimer la tâche"
				description="Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible."
				confirmText="Supprimer"
				onConfirm={handleConfirmDelete}
				isLoading={deleteTaskMutation.isPending}
				variant="danger"
			/>

			{isAnalyzeGfaSubmissionModalOpen && (
				<AnalyzeGFASubmissionModal
					open={isAnalyzeGfaSubmissionModalOpen}
					onOpenChange={setIsAnalyzeGfaSubmissionModalOpen}
					gfaId={entityId}
				/>
			)}
			{isGenerateGfaDocumentModalOpen && (
				<GenerateGFAConstructionSiteDocumentsModal
					types={[
						"DEVIS_GFA",
						"RAPPORT_EXPERTISE_GFA",
						"INDICATION_TARIFAIRE_GFA_CARAVELA",
					]}
					open={isGenerateGfaDocumentModalOpen}
					onOpenChange={setIsGenerateGfaDocumentModalOpen}
					gfaConstructionSiteId={entityId}
					onSuccess={async () => {
						await queryClient.invalidateQueries({
							queryKey: ["gfas", "tasks", entityIdString],
							refetchType: "active",
						});
						clearTaskSelection();
						clearPanel();
					}}
				/>
			)}
			{isUploadDocumentModalOpen && (
				<TaskUploadDocumentModal
					open={isUploadDocumentModalOpen}
					onOpenChange={setIsUploadDocumentModalOpen}
					onUpload={handleAttachDocument}
					isPending={attachDocumentMutation.isPending}
				/>
			)}
		</>
	);
};

export default ConstructionSiteTaskDetailsPanel;
